frappe.provide("erpnext.accounts"); // @dokos

/**
 * QuickPaymentPanel – a slide-in side panel for recording a Payment Entry
 * directly from a submitted Sales Invoice or Purchase Invoice form.
 *
 * Usage:
 *   new erpnext.accounts.QuickPaymentPanel({
 *       frm: this.frm,
 *       make_payment_entry_fn: this.make_payment_entry.bind(this),
 *   });
 *
 * The backend method is auto-derived from the doctype:
 *   Sales Invoice   → sales_invoice.make_quick_payment_entry
 *   Purchase Invoice → purchase_invoice.make_quick_payment_entry
 *
 * Beyond the simple same-currency receipt, the panel also handles:
 *   - Credit / debit notes (negative outstanding): refund / receipt, partial allowed.
 *   - Foreign-currency payments: the amount is entered in the bank account currency
 *     and the exchange gain/loss is booked server-side.
 *   - Bank charges: recorded as a non-FX deduction.
 */
erpnext.accounts.QuickPaymentPanel = class QuickPaymentPanel {
	constructor({ frm, make_payment_entry_fn }) {
		this.frm = frm;
		this.make_payment_entry_fn = make_payment_entry_fn;
		this.controls = {};
		this.wraps = {};

		const doc = this.frm.doc;
		this.invoiceCurrency = doc.currency;
		this.companyCurrency = erpnext.get_currency(doc.company);
		// Until a Mode of Payment is picked we assume the bank account is in the
		// invoice currency (no FX). The currency is resolved on mode_of_payment change.
		this.bankCurrency = doc.currency;
		this.isReturn = flt(doc.outstanding_amount) < 0;

		this.overlay = new frappe.views.dodock_drawer({
			animation_type: "slide",
			backdrop: true,
			open_width: frappe.is_mobile() ? "100vw" : "380px",
			parent: $(this.frm.page.wrapper).find(".page-overlay"),
		});

		this._render_header();
		this._render_body();
		this._render_footer();
		this._refresh_fx_visibility();
	}

	// ── DOM construction ──────────────────────────────────────────────────

	_render_header() {
		const doc = this.frm.doc;

		this.overlay.titleSection.textContent = "";
		this.overlay.titleSection.classList.add("qpp-header-meta");

		this._refEl = document.createElement("div");
		this._refEl.className = "qpp-invoice-ref";
		this._refEl.textContent = doc.name;

		// Works for both Sales Invoice (customer) and Purchase Invoice (supplier)
		this._partyEl = document.createElement("div");
		this._partyEl.className = "qpp-party-name";
		this._partyEl.textContent =
			doc.customer_name || doc.customer || doc.supplier_name || doc.supplier || "";

		this.overlay.titleSection.appendChild(this._refEl);
		this.overlay.titleSection.appendChild(this._partyEl);

		this._badgeEl = document.createElement("span");
		this._badgeEl.className = "qpp-outstanding-badge";
		this._badgeEl.textContent = format_currency(doc.outstanding_amount, doc.currency);
		this.overlay.rightSection.insertBefore(this._badgeEl, this.overlay.closingButton);

		this.overlay.topSection.style.background = "var(--subtle-fg)";
	}

	_render_body() {
		this.overlay.mainSection.className =
			"list-overlay-body flex-grow-1 overflow-auto p-4 d-flex flex-column";
		this.overlay.mainSection.style.gap = "1rem";

		const titleEl = document.createElement("div");
		titleEl.className = "qpp-section-title";
		titleEl.textContent = this.isReturn ? __("Record Refund") : __("Record Payment");
		this.overlay.mainSection.appendChild(titleEl);

		this._add_control({
			fieldtype: "Link",
			fieldname: "mode_of_payment",
			label: __("Mode of Payment"),
			options: "Mode of Payment",
			get_query: () => ({ filters: { enabled: 1 } }),
			onchange: () => this._on_mode_of_payment_change(),
		});

		this._add_control({
			fieldtype: "Currency",
			fieldname: "paid_amount",
			label: __("Amount"),
			options: this.bankCurrency,
			default: Math.abs(flt(this.frm.doc.outstanding_amount)),
			reqd: 1,
		});

		// Bank → company exchange rate. Only relevant when the bank account is not in
		// the company currency; toggled in _refresh_fx_visibility().
		this._add_control({
			fieldtype: "Float",
			fieldname: "exchange_rate",
			label: __("Exchange Rate"),
			precision: 9,
		});

		this._add_control({
			fieldtype: "Date",
			fieldname: "posting_date",
			label: __("Date"),
			default: frappe.datetime.nowdate(),
			reqd: 1,
		});

		this._add_control({
			fieldtype: "Currency",
			fieldname: "bank_charges_amount",
			label: __("Bank Charges"),
			options: this.companyCurrency,
		});

		this._add_control({
			fieldtype: "Link",
			fieldname: "bank_charges_account",
			label: __("Bank Charges Account"),
			options: "Account",
			get_query: () => ({
				filters: { company: this.frm.doc.company, is_group: 0 },
			}),
		});

		this._add_control({
			fieldtype: "Data",
			fieldname: "reference_no",
			label: __("Reference No"),
			placeholder: __("Optional"),
		});
	}

	_render_footer() {
		this.overlay.bottomSection.className = "list-overlay-footer border-top p-3 d-flex flex-column";
		this.overlay.bottomSection.style.gap = "0.5rem";

		this._submitBtn = document.createElement("button");
		this._submitBtn.className = "btn btn-primary qpp-submit-btn";
		this._submitBtn.textContent = this._submit_label();
		this._submitBtn.addEventListener("click", () => this._submit());
		this.overlay.bottomSection.appendChild(this._submitBtn);

		// Secondary button – opens the full Payment Entry form
		if (this.make_payment_entry_fn) {
			const fullFormBtn = document.createElement("button");
			fullFormBtn.className = "btn btn-secondary qpp-full-form-btn";
			fullFormBtn.textContent = __("Open Full Form");
			fullFormBtn.addEventListener("click", () => {
				this.hide();
				this.make_payment_entry_fn();
			});
			this.overlay.bottomSection.appendChild(fullFormBtn);
		}
	}

	_submit_label() {
		if (this.isReturn) {
			return this.frm.doc.doctype === "Sales Invoice" ? __("Record Refund") : __("Record Receipt");
		}
		return __("Save Payment Entry");
	}

	// ── Controls ──────────────────────────────────────────────────────────

	_add_control(df) {
		const wrap = document.createElement("div");
		wrap.className = "qpp-field";
		this.overlay.mainSection.appendChild(wrap);

		const ctrl = frappe.ui.form.make_control({
			df,
			parent: wrap,
			render_input: true,
		});
		ctrl.refresh();
		if (df.default != null) {
			ctrl.set_value(df.default);
		}
		this.controls[df.fieldname] = ctrl;
		this.wraps[df.fieldname] = wrap;
		return ctrl;
	}

	_toggle_field(fieldname, show) {
		if (this.wraps[fieldname]) {
			this.wraps[fieldname].style.display = show ? "" : "none";
		}
	}

	_relabel_currency_field(fieldname, currency) {
		const ctrl = this.controls[fieldname];
		if (!ctrl) return;
		ctrl.df.options = currency;
		ctrl.refresh();
	}

	_get_values() {
		const vals = {};
		Object.entries(this.controls).forEach(([k, c]) => (vals[k] = c.get_value()));
		return vals;
	}

	// ── Currency / FX handling ──────────────────────────────────────────────

	get _fx_active() {
		return this.bankCurrency && this.bankCurrency !== this.invoiceCurrency;
	}

	get _needs_exchange_rate() {
		return this.bankCurrency && this.bankCurrency !== this.companyCurrency;
	}

	_refresh_fx_visibility() {
		this._relabel_currency_field("paid_amount", this.bankCurrency);
		this._toggle_field("exchange_rate", this._fx_active && this._needs_exchange_rate);
	}

	async _on_mode_of_payment_change() {
		const mop = this.controls.mode_of_payment.get_value();

		if (!mop) {
			this.bankCurrency = this.invoiceCurrency;
			this._refresh_fx_visibility();
			return;
		}

		const account = await frappe.xcall(
			"erpnext.accounts.doctype.journal_entry.journal_entry.get_default_bank_cash_account",
			{ company: this.frm.doc.company, mode_of_payment: mop, account_type: "Bank" }
		);

		this.bankCurrency = account?.account_currency || this.invoiceCurrency;

		// Default the bank-currency amount and the exchange rate sensibly.
		if (this._fx_active) {
			if (this.bankCurrency === this.companyCurrency) {
				// Bank in company currency: the amount is the invoice's base value.
				this.controls.paid_amount.set_value(
					Math.abs(flt(this.frm.doc.outstanding_amount) * flt(this.frm.doc.conversion_rate))
				);
			}
			if (this._needs_exchange_rate) {
				await this._fetch_exchange_rate();
			}
		} else {
			this.controls.paid_amount.set_value(Math.abs(flt(this.frm.doc.outstanding_amount)));
		}

		// Pre-fill the bank charges account from the Mode of Payment fee account.
		const fee_account = await frappe
			.xcall("erpnext.accounts.doctype.mode_of_payment.mode_of_payment.get_fee_account", {
				mode_of_payment: mop,
				company: this.frm.doc.company,
			})
			.catch(() => null);
		if (fee_account && !this.controls.bank_charges_account.get_value()) {
			this.controls.bank_charges_account.set_value(fee_account);
		}

		this._refresh_fx_visibility();
	}

	async _fetch_exchange_rate() {
		const args = this.frm.doc.doctype === "Sales Invoice" ? "for_selling" : "for_buying";
		const rate = await frappe.xcall("erpnext.setup.utils.get_exchange_rate", {
			transaction_date: this.controls.posting_date.get_value() || frappe.datetime.nowdate(),
			from_currency: this.bankCurrency,
			to_currency: this.companyCurrency,
			args,
		});
		if (rate) {
			this.controls.exchange_rate.set_value(rate);
		}
	}

	// ── Visibility ────────────────────────────────────────────────────────

	show() {
		this.overlay.open();
		this.controls.paid_amount?.$input?.focus();
	}

	hide() {
		this.overlay.close();
	}

	destroy() {
		this.overlay.close();
		$(this.overlay.wrapper).remove();
	}

	// ── Submit ────────────────────────────────────────────────────────────

	async _submit() {
		const vals = this._get_values();
		const errors = [];

		if (!vals.paid_amount || flt(vals.paid_amount) <= 0) errors.push(__("Amount must be greater than 0"));
		if (!vals.posting_date) errors.push(__("Date is required"));
		if (this._fx_active && this._needs_exchange_rate && !vals.exchange_rate)
			errors.push(__("Exchange Rate is required"));
		if (flt(vals.bank_charges_amount) > 0 && !vals.bank_charges_account)
			errors.push(__("Bank Charges Account is required"));

		if (errors.length) {
			frappe.msgprint({ title: __("Validation"), indicator: "red", message: errors.join("<br>") });
			return;
		}

		this._submitBtn.disabled = true;
		this._submitBtn.textContent = __("Saving…");

		try {
			// Derive the backend method and param name from the doctype:
			//   "Sales Invoice"   → method: "…sales_invoice.make_quick_payment_entry", param: "sales_invoice"
			//   "Purchase Invoice" → method: "…purchase_invoice.make_quick_payment_entry", param: "purchase_invoice"
			const doctype_key = this.frm.doc.doctype.toLowerCase().replace(/ /g, "_");
			const method = `erpnext.accounts.doctype.${doctype_key}.${doctype_key}.make_quick_payment_entry`;

			// When the bank account is in a foreign currency the entered amount is the
			// bank-currency figure (received_amount / paid_amount), so it travels as
			// bank_amount and the party side is settled in full. Otherwise it is the
			// invoice-currency amount and partial payments / refunds are allowed.
			const fx = this._fx_active;
			const payload = {
				[doctype_key]: this.frm.doc.name,
				posting_date: vals.posting_date,
				paid_amount: fx ? null : flt(vals.paid_amount),
				mode_of_payment: vals.mode_of_payment || null,
				reference_no: vals.reference_no || null,
				bank_amount: fx ? flt(vals.paid_amount) : null,
				exchange_rate: fx && this._needs_exchange_rate ? flt(vals.exchange_rate) : null,
				bank_charges_amount: flt(vals.bank_charges_amount) || null,
				bank_charges_account: flt(vals.bank_charges_amount) ? vals.bank_charges_account : null,
			};

			// Drop null keys: form-encoded requests turn a JS `null` into the empty
			// string "", which fails the backend's `float | None` type validation and
			// leaks "" into `str | None` params. Omitting them lets the Python defaults apply.
			for (const key of Object.keys(payload)) {
				if (payload[key] === null) delete payload[key];
			}

			const result = await frappe.xcall(method, payload);

			if (result?.name) {
				frappe.show_alert(
					{
						message: __("Payment Entry {0} saved", [
							`<a href="/desk/payment-entry/${result.name}">${result.name}</a>`,
						]),
						indicator: "green",
					},
					6
				);
				this.hide();
				this.frm.reload_doc();
			}
		} finally {
			this._submitBtn.disabled = false;
			this._submitBtn.textContent = this._submit_label();
		}
	}
};
